import { isValidPhoneNumber } from 'react-phone-number-input';

/**
 *
 *  VALIDATION SIRET/SIREN
 *
 * */

const re = {
  SIRET: /^\d{14}$/,
  SIREN: /^\d{9}$/,
  formatSIRET: /^(\d{3})(\d{3})(\d{3})(\d{5})$/g,
  formatSIREN: /(\d\d\d\B)/g,
  deformatSIRET_SIREN: /\s/g,
};

/**
 *  Algorithm de Luhn validation SIRET et SIREN
 */

function checkLuhn(value: string): boolean {
  let digit: number;
  let sum = 0;
  let even = false;
  for (let i = value.length - 1; i >= 0; i -= 1) {
    digit = parseInt(value[i], 10);
    if (even) {
      digit += digit;
      if (digit > 9) {
        digit -= 9;
      }
    }
    sum += digit;
    even = !even;
  }
  return sum % 10 === 0;
}

/**
 *
 * Principe : on multiplie les chiffres de rang impair par 1 ; ceux de rang pair par 2.
 *
 * Si la somme des chiffres obtenus est un multiple de 10, le numéro SIREN est valide.
 *
 * Exemple : le numéro SIREN = 379 158 322
 *
 * --------------------------------------------------------------
 * Rang paire     |    | 14 |   | 2 |   | 16 |   | 4 |   |
 * --------------------------------------------------------------
 * Rang impaire  | 3  |    | 9 |   | 5 |    | 3 |   | 2 |
 * --------------------------------------------------------------
 * Somme        | 3 + 1 + 4 + 9 + 2 + 5 + 1 + 6 + 3 + 4 + 2 = 40
 * --------------------------------------------------------------
 *
 */
export function isSIREN(siren: string): boolean {
  return re.SIREN.test(siren) && checkLuhn(siren);
}

/**
 *
 * La méthode de vérification du SIRET ressemble à celle du SIREN;
 * mais à l'inverse du contrôle du SIREN, on multiplie les chiffres de rang impair par 2, ceux de rang pair par 1.
 *
 * Si la somme des chiffres obtenus est un multiple de 10, le numéro SIRET est valide.
 *
 * Exemple : le numéro SIRET = 379 158 322 00097
 *
 * --------------------------------------------------------------------------------------
 * Rang paire     |  | 7 |    | 1 |    | 8 |   | 2 |   | 0 |   | 0 |    | 7 |
 * --------------------------------------------------------------------------------------
 * Rang impaire  | 6 |  | 18 |   | 10 |   | 6 |   | 4 |   | 0 |   | 18 |   |
 * --------------------------------------------------------------------------------------
 * Somme        | 6 + 7 + 1 + 8 + 1 + 1 + 0 + 8 + 6 + 2 + 4 + 0 + 0 + 0 + 1 + 8 + 7 = 60
 * --------------------------------------------------------------------------------------
 *
 */
export function isSIRET(siret: string): boolean {
  return re.SIRET.test(siret) && checkLuhn(siret);
}

export function formatSIRET(value: string): string {
  if (!isSIRET(value)) {
    return value;
  }
  return value.replace(re.formatSIRET, '$1 $2 $3 $4');
}

export function formatSIREN(value: string): string {
  if (!isSIREN(value)) {
    return value;
  }
  return value.replace(re.formatSIREN, '$1 ');
}

export function deformatSIRETSIREN(value: string): string {
  return value.replace(re.deformatSIRET_SIREN, '');
}

/***
 *
 *  END VALIDATION SIRET/SIREN
 *
 */

export const isEmailValid = (email: string): boolean => {
  if (isContainSpaces(email)) return false;
  const regexEmail = /\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/;
  return email !== undefined && regexEmail.test(email.trim());
};

export const isContainSpaces = (args: string) => {
  // return /\s/.test(args.trim());
  return args.trim().indexOf(' ') >= 0;
};

export const isValidInput = (type: string, value: any, required?: boolean) => {
  if (required && !value) return false;
  switch (type) {
    case 'email':
      return isEmailValid((value as string) ?? '');
    case 'tel':
      return isValidPhoneNumber((value as string) ?? '');
    default:
      return true;
  }
};
